import { useCallback, useState } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import { Volume2, X } from "lucide-react";
import { ensureMobileSpeakerOutput } from "./mobileSpeakerRouting";

type MobileLowAudioBannerProps = {
    meetingClient: RealtimeKitClient | null;
    mobileOS: "ios" | "android" | "other";
    /** Só mostra na aba Cenário, onde o áudio costuma ir pro auricular. */
    showOnSceneTab?: boolean;
};

const MobileLowAudioBanner: React.FC<MobileLowAudioBannerProps> = ({
    meetingClient,
    mobileOS,
    showOnSceneTab = true,
}) => {
    const [dismissed, setDismissed] = useState(false);
    const [busy, setBusy] = useState(false);

    const handleBoost = useCallback(async () => {
        if (!meetingClient || busy) {
            return;
        }

        setBusy(true);
        try {
            await ensureMobileSpeakerOutput(meetingClient, {
                iosMicPulse: mobileOS === "ios",
            });
        } finally {
            setBusy(false);
        }
    }, [busy, meetingClient, mobileOS]);

    if (
        dismissed ||
        !meetingClient ||
        !showOnSceneTab ||
        (mobileOS !== "ios" && mobileOS !== "android")
    ) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto absolute bottom-[calc(7.5rem+env(safe-area-inset-bottom,0px))] left-3 right-3 z-40 mx-auto flex max-w-sm items-center gap-2 rounded-xl border border-blue-500/40 bg-slate-900/95 px-3 py-2.5 text-white shadow-lg backdrop-blur-sm"
        >
            <Volume2 className="h-4 w-4 shrink-0 text-blue-400" aria-hidden />
            <p className="min-w-0 flex-1 text-[11px] leading-snug text-slate-200">
                Áudio baixo no celular? Toque para forçar o alto-falante.
            </p>
            <button
                type="button"
                onClick={() => void handleBoost()}
                disabled={busy}
                className="shrink-0 rounded-lg bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-60"
            >
                {busy ? "…" : "Ativar"}
            </button>
            <button
                type="button"
                onClick={() => setDismissed(true)}
                className="shrink-0 rounded p-0.5 text-slate-500 hover:text-slate-300"
                aria-label="Fechar aviso de áudio"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
};

export default MobileLowAudioBanner;
